import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/axios';
import { formatIndianDateTime } from '../utils/format';

const roleClass = (role) => {
  const r = String(role || '').toUpperCase();
  if (r === 'ADMIN' || r === 'BDM') return 'pmb-role-manager';
  if (r === 'SALES') return 'pmb-role-sales';
  if (r === 'DEVELOPER') return 'pmb-role-dev';
  return 'pmb-role-other';
};

const PMBBoard = ({ user }) => {
  const navigate = useNavigate();
  const { projectId } = useParams();

  const [loading, setLoading] = useState(true);
  const [board, setBoard] = useState({ board_id: null, members: [] });
  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');

  const isManager = user.role === 'ADMIN' || user.role === 'BDM';

  const totalPages = useMemo(() => Math.max(Math.ceil(total / limit) || 1, 1), [total, limit]);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const initials = (name) => {
    const n = String(name || '').trim();
    if (!n) return 'U';
    const parts = n.split(/\s+/).filter(Boolean);
    const a = parts[0]?.[0] || 'U';
    const b = parts.length > 1 ? parts[parts.length - 1]?.[0] : '';
    return `${a}${b}`.toUpperCase();
  };

  const dayLabel = (d) => new Intl.DateTimeFormat('en-IN', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' }).format(d);

  const threadItems = useMemo(() => {
    const list = Array.isArray(messages) ? messages : [];
    const items = [];
    let lastKey = null;
    for (const m of list) {
      const ts = m.created_at ? new Date(m.created_at) : null;
      const key = ts && !Number.isNaN(ts.getTime()) ? `${ts.getFullYear()}-${ts.getMonth()}-${ts.getDate()}` : null;
      if (key && key !== lastKey) {
        items.push({ type: 'divider', key: `d-${key}`, label: dayLabel(ts) });
        lastKey = key;
      }
      items.push({ type: 'msg', key: `m-${m.id}`, msg: m });
    }
    return items;
  }, [messages]);

  const fetchBoard = async () => {
    const res = await api.get(`/api/projects/${projectId}/board`);
    setBoard(res.data || { board_id: null, members: [] });
  };

  const fetchMessages = async (nextPage = page) => {
    const res = await api.get(`/api/projects/${projectId}/board/messages`, { params: { page: nextPage, limit } });
    const payload = res.data;
    setMessages(payload.items || []);
    setTotal(payload.total || 0);
    setPage(payload.page || nextPage);
  };

  const bootstrap = async () => {
    setLoading(true);
    setError('');
    try {
      await fetchBoard();
      await fetchMessages(1);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load board');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const initBoard = async () => {
    setError('');
    try {
      await api.post(`/api/projects/${projectId}/board/init`);
      await bootstrap();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to initialize board');
    }
  };

  const onFilesChange = (e) => {
    const list = Array.from(e.target.files || []);
    setFiles(list);
  };

  const removeFileAt = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const submitMessage = async (e) => {
    e.preventDefault();
    setPosting(true);
    setError('');

    try {
      const fd = new FormData();
      fd.append('message_text', text);
      files.forEach((f) => fd.append('files', f));

      await api.post(`/api/projects/${projectId}/board/messages`, fd);
      setText('');
      setFiles([]);
      await fetchMessages(1);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to post message');
    } finally {
      setPosting(false);
    }
  };

  const downloadAttachment = async (att) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/board/attachments/${att.id}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) {
        let msg = 'Download failed';
        try {
          const j = await res.json();
          msg = j?.error || msg;
        } catch (e) {
        }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.original_name || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Download failed');
    }
  };

  if (loading) {
    return <div className="text-center p-8">Loading board...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">PMB Discussion</h1>
          <div className="text-sm text-tertiary">Project #{projectId}</div>
        </div>
        <div className="flex gap-2">
          <button type="button" className="glass-button" onClick={() => navigate('/projects')}>Back</button>
          {board.board_id ? null : (
            isManager ? (
              <button type="button" className="glass-button primary" onClick={initBoard}>Init Board</button>
            ) : (
              <button type="button" className="glass-button" disabled>Waiting for board init</button>
            )
          )}
        </div>
      </div>

      {error ? (
        <div className="glass-card p-4 mb-4" style={{ borderColor: 'rgba(255,80,80,0.35)' }}>
          <div className="text-sm" style={{ color: 'rgba(255,120,120,0.95)' }}>{error}</div>
        </div>
      ) : null}

      {board.board_id ? (
        <div className="glass-card p-4 mb-6">
          <div className="text-sm text-secondary">Members: {board.members?.length || 0}</div>
        </div>
      ) : null}

      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-tertiary">Messages ({total})</div>
        {totalPages > 1 ? (
          <div className="flex gap-2">
            <button type="button" className="glass-button" onClick={() => fetchMessages(page - 1)} disabled={!canPrev}>
              Prev
            </button>
            <button type="button" className="glass-button" onClick={() => fetchMessages(page + 1)} disabled={!canNext}>
              Next
            </button>
          </div>
        ) : null}
      </div>

      <div className="chat-panel">
        <div className="chat-thread">
          {messages.length === 0 ? (
            <div className="glass-card p-6 text-center text-secondary">No messages yet</div>
          ) : (
            <div className="chat-list">
              {threadItems.map((it) => {
                if (it.type === 'divider') {
                  return (
                    <div key={it.key} className="chat-date-divider">
                      <span>{it.label}</span>
                    </div>
                  );
                }
                const m = it.msg;
                const mine = String(m.created_by) === String(user.id);
                return (
                  <div key={it.key} className={`chat-row ${mine ? 'mine' : 'theirs'}`}>
                    <div className="chat-avatar" aria-hidden="true">
                      {initials(m.created_by_name)}
                    </div>
                    <div className={`chat-bubble ${roleClass(m.created_by_role)}`}>
                      <div className="chat-meta">
                        <div className="chat-name">{m.created_by_name || 'User'}</div>
                        <span className={`role-pill role-${String(m.created_by_role || '').toLowerCase()}`}>{m.created_by_role}</span>
                        <span className="chat-time">{formatIndianDateTime(m.created_at)}</span>
                      </div>
                      {m.message_text ? <div className="chat-text whitespace-pre-wrap">{m.message_text}</div> : null}
                      {m.attachments && m.attachments.length ? (
                        <div className="chat-attachments">
                          {m.attachments.map((a) => (
                            <div key={a.id} className="chat-attachment">
                              <div className="min-w-0">
                                <div className="text-sm font-medium truncate">{a.original_name}</div>
                                <div className="text-xs text-tertiary">{a.mime_type || 'file'}{a.size_bytes ? ` • ${Math.round(a.size_bytes / 1024)} KB` : ''}</div>
                              </div>
                              <button type="button" className="glass-button text-sm px-3 py-1" onClick={() => downloadAttachment(a)}>
                                Download
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="chat-composer">
          <form onSubmit={submitMessage} className="grid gap-3">
            <textarea
              className="glass-textarea w-full"
              rows={3}
              placeholder="Post project requirements, notes, updates..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={!board.board_id || posting}
            />

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <input
                type="file"
                multiple
                onChange={onFilesChange}
                disabled={!board.board_id || posting}
              />

              <button type="submit" className="glass-button primary" disabled={!board.board_id || posting || (!text && files.length === 0)}>
                {posting ? 'Posting...' : 'Post'}
              </button>
            </div>

            {files.length ? (
              <div className="pmb-files">
                {files.map((f, idx) => (
                  <div key={`${f.name}-${idx}`} className="pmb-file">
                    <span className="pmb-file-name">{f.name}</span>
                    <button type="button" className="glass-button text-sm px-3 py-1" onClick={() => removeFileAt(idx)} disabled={posting}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </form>
        </div>
      </div>
    </div>
  );
};

export default PMBBoard;
